import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize, Download } from 'lucide-react';
import { useLang } from './i18n';

export function VideoPlayer({ src, name }: { src: string; name?: string }) {
  const [, , tr] = useLang();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const lastAudibleVolume = useRef(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onTimeUpdate = () => setCurrentTime(v.currentTime);
    setPlaying(false); setCurrentTime(0); setDuration(0);
    const onLoadedMetadata = () => setDuration(Number.isFinite(v.duration) ? v.duration : 0);
    const onVolumeChange = () => { setVolume(v.volume); setMuted(v.muted); if (v.volume > 0) lastAudibleVolume.current = v.volume; };
    const onFullscreenChange = () => setFullscreen(document.fullscreenElement === containerRef.current);
    const onEnded = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    v.addEventListener('timeupdate', onTimeUpdate);
    v.addEventListener('loadedmetadata', onLoadedMetadata);
    v.addEventListener('ended', onEnded);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('volumechange', onVolumeChange);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    onVolumeChange();

    return () => {
      v.removeEventListener('timeupdate', onTimeUpdate);
      v.removeEventListener('loadedmetadata', onLoadedMetadata);
      v.removeEventListener('ended', onEnded);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('volumechange', onVolumeChange);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [src]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => {});
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.muted || video.volume === 0) {
      if (video.volume === 0) video.volume = lastAudibleVolume.current;
      video.muted = false;
    } else video.muted = true;
  };

  const changeVolume = (value: number) => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(value)) return;
    const next = Math.max(0, Math.min(1, value / 100));
    video.volume = next;
    video.muted = false;
    // Controlled range inputs must update in this event; volumechange arrives later.
    setVolume(next);
    setMuted(false);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    videoRef.current.currentTime = percent * duration;
    setCurrentTime(percent * duration);
  };

  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (playing) {
      hideTimerRef.current = setTimeout(() => setShowControls(false), 2400);
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className={`custom-video-player ${fullscreen ? 'is-fullscreen' : ''}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => playing && setShowControls(false)}
    >
      <video
        ref={videoRef}
        className="video-element"
        src={src}
        preload="metadata"
        playsInline
        onClick={togglePlay}
      />

      {/* Big center play icon if paused */}
      {!playing && (
        <button type="button" className="video-big-play" onClick={togglePlay} aria-label={tr('Play', 'Abspielen')}>
          <Play size={28} fill="white" />
        </button>
      )}

      {/* Video controls bar */}
      <div className={`video-controls ${showControls || !playing ? 'is-visible' : ''}`}>
        {/* Progress seeker */}
        <div className="video-seekbar-wrap" onClick={handleSeek}>
          <div className="video-seekbar-bg">
            <div className="video-seekbar-fill" style={{ width: `${progress}%` }} />
            <div className="video-seekbar-thumb" style={{ left: `${progress}%` }} />
          </div>
        </div>

        <div className="video-controls-row">
          <div className="video-controls-left">
            <button type="button" className="video-btn" onClick={togglePlay} aria-label={playing ? tr('Pause', 'Pause') : tr('Play', 'Abspielen')}>
              {playing ? <Pause size={18} /> : <Play size={18} fill="currentColor" />}
            </button>
            <div className="video-volume">
              <button type="button" className="video-btn" onClick={toggleMute} aria-label={muted || volume === 0 ? tr('Unmute', 'Ton einschalten') : tr('Mute', 'Stummschalten')}>
                {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <input className="video-volume-slider" type="range" min="0" max="100" step="1" value={muted ? 0 : Math.round(volume * 100)} aria-label={tr('Video volume', 'Videolautstärke')} aria-valuetext={`${muted ? 0 : Math.round(volume * 100)} %`} onChange={e => changeVolume(Number(e.currentTarget.value))} />
            </div>
            <span className="video-time">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className="video-controls-right">
            {name && (
              <a href={src} download={name} className="video-btn" title={tr('Download', 'Herunterladen')} aria-label={tr('Download', 'Herunterladen')}>
                <Download size={16} />
              </a>
            )}
            <button type="button" className="video-btn" onClick={toggleFullscreen} aria-label={fullscreen ? tr('Exit fullscreen', 'Vollbild verlassen') : tr('Fullscreen', 'Vollbild')}>
              {fullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
